import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import {
  IntellioVisualization3DSpace,
  type IntellioChartPanel,
  type IntellioVisualization3DSpaceModel,
} from "@triedge/ai-assistant-ui";
import type { AdmissionsAssistantArtifactWorkspaceRef } from "./types";
import {
  resolveAssistantVisualization3DArtifactState,
  type AssistantVisualization3DArtifactStateResolver,
} from "./assistantVisualization3DArtifactState";
import { SupportErrorMessage } from "../overlays/EngineeringSupportBanner";

type AssistantVisualization3DWorkspaceProps = {
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialSpace?: IntellioVisualization3DSpaceModel | null;
  initialPanels?: IntellioChartPanel[];
  onClose: () => void;
  presentation?: "portal" | "embedded";
  resolveVisualization3DArtifactState?: AssistantVisualization3DArtifactStateResolver;
};

export const AssistantVisualization3DWorkspace: React.FC<
  AssistantVisualization3DWorkspaceProps
> = ({
  visualization3DArtifact,
  initialSpace = null,
  initialPanels = [],
  onClose,
  presentation = "portal",
  resolveVisualization3DArtifactState,
}) => {
  const [space, setSpace] = useState<IntellioVisualization3DSpaceModel | null>(
    initialSpace,
  );
  const [panels, setPanels] = useState<IntellioChartPanel[]>(initialPanels);
  const [isLoading, setIsLoading] = useState<boolean>(!initialSpace);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const artifactRefreshKey =
    visualization3DArtifact.sourceRef ?? visualization3DArtifact.downloadUrl;
  const resolveStateRef = useRef(resolveVisualization3DArtifactState);
  resolveStateRef.current = resolveVisualization3DArtifactState;

  useEffect(() => {
    let cancelled = false;
    setSpace(initialSpace);
    setPanels(initialPanels);
    setIsLoading(!initialSpace);
    setErrorMessage(null);
    void (async () => {
      try {
        const resolvedState = await resolveAssistantVisualization3DArtifactState({
          visualization3DArtifact,
          initialSpace,
          initialPanels,
          refreshVisualization3DArtifactState: resolveStateRef.current,
          forceRefresh: !initialSpace,
        });
        if (cancelled) {
          return;
        }
        setSpace(resolvedState.visualization3DSpace);
        setPanels(resolvedState.visualization3DPanels);
        setErrorMessage(null);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Unable to load 3D visualization workspace.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [artifactRefreshKey, initialPanels.length, initialSpace]);

  const body = (
    <div
      className={presentation === "portal"
        ? "fixed inset-0 z-[1000] bg-slate-950/45 p-4 backdrop-blur-sm"
        : "h-full min-h-0 bg-[#F8FAF9]"}
    >
      <div className={presentation === "portal"
        ? "mx-auto flex h-full max-w-7xl flex-col"
        : "flex h-full min-h-0 flex-col"}
      >
        {isLoading ? (
          <div className="flex min-h-[360px] flex-1 items-center justify-center rounded-lg border border-[#CDEEE9] bg-white text-[#496A65]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span className="text-sm font-semibold">Loading 3D visualization</span>
          </div>
        ) : errorMessage || !space ? (
          <SupportErrorMessage>
            {errorMessage ?? "The saved visualization artifact did not include a valid space payload."}
          </SupportErrorMessage>
        ) : (
          <IntellioVisualization3DSpace
            space={space}
            fallbackPanels={panels}
            variant="fullscreen"
            onClose={onClose}
            className="min-h-0 flex-1"
          />
        )}
      </div>
    </div>
  );

  if (presentation === "embedded") {
    return body;
  }
  if (typeof document === "undefined") {
    return null;
  }
  return createPortal(body, document.body);
};
